var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useMemo, useState } from 'react';
import { useCssContext } from '../CssStore';
/**
 * Creates an esi:include tag with the given src
 * @param src the url for the esi
 */
export var createIncludeElement = function (src) {
    return "<esi:include data-testid=\"esi\" src=\"" + src + "\" />";
};
/**
 * Tries to load/reload a given module by calling require.undef and require
 * @param module the module string as needed by requirejs
 */
var loadModule = function (module) {
    window.require.undef(window.findActualPathFromModuleName(module));
    window.require([module]);
};
/**
 * The config set is a single list of IS7 services that we want the configuration for.
 * This is currently only resolved once at the start which will catch serverside ESIs but the required modules might also change with clientside ESIs.
 * As this is a single path we can't really influence we need to throw the whole thing away and reload it
 */
var reloadConfigSet = function () {
    if (window && window.requirejs && window.getConfigSetURI) {
        var configSetURI = window.getConfigSetURI();
        if (configSetURI === window.configSetURI) {
            return;
        }
        // This needs to be injected directly into the config
        window.requirejs.config({
            paths: {
                configSet: configSetURI,
            },
        });
        // undef both options here, just in case
        window.require.undef('configSet');
        window.require.undef(window.configSetURI);
        loadModule('configSet');
        window.configSetURI = configSetURI;
    }
};
export var makeScriptFinder = function () {
    return new RegExp(/<script[^>]*type="text\/javascript"[^>]*>([\s\S]+?)<\/script>/gi);
};
export var makeComponentFinder = function () {
    return new RegExp(/data-component-uri="([a-zA-Z0-9/\-_?=&;.$]+)/g);
};
export var makeMainFinder = function () { return new RegExp(/data-main="([a-zA-Z0-9/\-_?=&;.$]+)/g); };
/**
 * This component provides a solution for `<esi>` tags that live inside the react component tree. It supports server side loading by generating the
 * correct `esi:include` tags and client side loading by fetching the url and inserting the response body in the dom.
 * @param src the url of the esi
 */
export var srcToClass = function (src) { return src.replace(/\?|=|&|:|;|\.|\$|\//g, '-'); };
export var Esi = function (_a) {
    var src = _a.src, callback = _a.callback, className = _a.className;
    var _b = useState(''), body = _b[0], setBody = _b[1];
    var addCssPath = useCssContext();
    useMemo(function () { return __awaiter(void 0, void 0, void 0, function () {
        var scriptFinder, scripts, parser, xmlDoc, externalScripts, _loop_1, _i, externalScripts_1, script, componentFinder, mainFinder, components, mainFiles;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(typeof window !== 'undefined')) return [3 /*break*/, 7];
                    if (!(body === '')) return [3 /*break*/, 1];
                    fetch(src, {
                        method: 'GET',
                    })
                        .then(function (response) { return response.text(); })
                        .then(function (responseBody) {
                        // I am way too bad at this to solve this with regex so we use a DOMParser here
                        var parser = new DOMParser();
                        var xmlDoc = parser.parseFromString(responseBody, 'text/html');
                        Array.prototype.slice
                            .call(xmlDoc.querySelectorAll('link[rel="stylesheet"]'))
                            // We know that this is a list of HTMLLinkElement since that is what we query for
                            .forEach(function (elem) {
                            var href = elem.href;
                            addCssPath(href);
                            if (elem.parentNode) {
                                // Remove the element from the xmlDoc
                                // Dangerous in-place modification happening here
                                elem.parentNode.removeChild(elem);
                            }
                        });
                        // Now the xmlDoc should be free of the link tags so back to a string so we can actually do something with that
                        // JS doesn't offer anything to get a representation where &,< and > aren't replaced for some reason so this works around that...
                        var htmlstring = xmlDoc.documentElement.outerHTML.replace(/&(amp|lt|gt);/gi, function (enc) {
                            switch (enc) {
                                case '&amp;':
                                    return '&';
                                case '&lt;':
                                    return '<';
                                case '&gt;':
                                    return '>';
                                default:
                                    return enc;
                            }
                        });
                        setBody(htmlstring);
                    });
                    return [3 /*break*/, 6];
                case 1:
                    scriptFinder = makeScriptFinder();
                    scripts = null;
                    // exec is stateful and only return one match per iteration. to get all of them we need to call this in a loop
                    // eslint-disable-next-line no-cond-assign
                    while ((scripts = scriptFinder.exec(body)) !== null) {
                        scripts.forEach(function (script) {
                            // This returns every match twice, once with the surrounding <script> tags and once without, we want the ones without the tags
                            if (script.indexOf('<') !== 0) {
                                // This is awful but we kind of lack options here
                                try {
                                    // eslint-disable-next-line no-eval
                                    eval(script);
                                }
                                catch (e) {
                                    // eslint-disable-next-line no-console
                                    console.error(e);
                                }
                            }
                        });
                    }
                    parser = new DOMParser();
                    xmlDoc = parser.parseFromString(body, 'text/html');
                    externalScripts = Array.prototype.slice.call(xmlDoc.querySelectorAll('script[src]'));
                    _loop_1 = function (script) {
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0: 
                                // eslint-disable-next-line no-await-in-loop
                                return [4 /*yield*/, new Promise(function (resolve) {
                                        var _a;
                                        try {
                                            // For any reason, we need to create a new script here
                                            var newScript = document.createElement('script');
                                            newScript.onload = function () { return resolve(); };
                                            newScript.onerror = function () { return resolve(); };
                                            newScript.src = script.src;
                                            // append to head to load
                                            document.head.appendChild(newScript);
                                            // Remove the element from the xmlDoc
                                            // Dangerous in-place modification happening here
                                            (_a = script.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(script);
                                        }
                                        catch (e) {
                                            resolve();
                                        }
                                    })];
                                case 1:
                                    // eslint-disable-next-line no-await-in-loop
                                    _a.sent();
                                    return [2 /*return*/];
                            }
                        });
                    };
                    _i = 0, externalScripts_1 = externalScripts;
                    _a.label = 2;
                case 2:
                    if (!(_i < externalScripts_1.length)) return [3 /*break*/, 5];
                    script = externalScripts_1[_i];
                    return [5 /*yield**/, _loop_1(script)];
                case 3:
                    _a.sent();
                    _a.label = 4;
                case 4:
                    _i++;
                    return [3 /*break*/, 2];
                case 5:
                    reloadConfigSet();
                    componentFinder = makeComponentFinder();
                    mainFinder = makeMainFinder();
                    components = componentFinder.exec(body);
                    if (components && components.length > 1) {
                        components.shift();
                        components.forEach(loadModule);
                    }
                    mainFiles = mainFinder.exec(body);
                    if (mainFiles && mainFiles.length > 1) {
                        mainFiles.shift();
                        mainFiles.forEach(loadModule);
                    }
                    _a.label = 6;
                case 6:
                    if (typeof callback === 'function') {
                        callback();
                    }
                    _a.label = 7;
                case 7: return [2 /*return*/];
            }
        });
    }); }, [src, body]);
    return (React.createElement("div", { className: srcToClass(src) + " " + (className || ''), 
        /* eslint-disable react/no-danger */
        dangerouslySetInnerHTML: {
            __html: typeof window === 'undefined' ? createIncludeElement(src) : body,
        }, "data-testid": srcToClass(src), "data-src": src }));
    /* eslint-enable react/no-danger */
};
//# sourceMappingURL=index.js.map